// §4.13's Tags card (P3-E2 PR E2-2a1, D-323 ruling 5): the tenant's tag list
// on the sales settings screen.
//
// WHO SEES IT. Every reader of the settings screen holds `sales.read`, and
// storekeeper and qc hold it too, but the tag read follows the customer book's
// audience (§5's E2-2 row 4), whose mirror is `CUSTOMER_SCREEN_ROLES`. For a
// lineage the mirror KNOWS is refused, the card is absent and no request is
// sent. A 403 for a reader the mirror cannot judge (a tenant's composed role)
// renders one sentence in the card's place, the spec's own words.
//
// WHO CHANGES IT. `sales.tier.manage` (group_admin, facility_admin) curates:
// New tag, rename, Archive. Everyone else sees the list without controls and a
// sentence saying who changes it. No route deletes a tag; Archive hides it
// from every picker and keeps its links, so the archived tags are listed only
// when the reader asks (Show archived reads `archived=all`), fifty at a time
// with Show more while the server names a next page.
//
// THE NAME. 1 to 40 characters after trim, never a `;` (the export joins names
// with `; ` and the import lane splits on `;`). The client refuses what the
// contract would, and the two 409s (`TAG_NAME_TAKEN`, `TAG_LIMIT_REACHED`)
// read on the field that caused them.
//
// ARCHIVE ASKS FIRST. No route makes an archived tag active again, and a new
// tag under the same name is a new id that the customers carrying the old one
// do not move to, so Archive opens an in-row confirm that says so, as the
// bulk Archive and Redact do.
//
// ONE KEY PER ATTEMPT (`lib/attempt-key.ts`). New tag keeps the typed name
// after a failure, so a second press after a lost response reuses the first
// press's key and the server replays its 201, rather than refusing the
// reader's own new tag as `TAG_NAME_TAKEN`. Typing starts a new attempt.
//
// TWO EMPTIES. An empty `none` read means no ACTIVE tag, which says nothing
// about archived ones, so it reads "No active tags" and points to Show
// archived; "No tags yet" is the empty `all` read alone.
//
// One primary per view (design-system's Button rule): the settings screen's
// page head holds Save, so New tag and the rename Save are secondary.
//
// OFFLINE. A read held because the browser has no network says so in the
// card, and New tag, rename, Archive and Show more are disabled.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../../components/button";
import { Card } from "../../../../../components/card";
import { HelpTip } from "../../../../../components/help-tip";
import { DataTable, type DataTableColumn } from "../../../../../components/data-table";
import { CheckboxField, Field } from "../../../../../components/field";
import {
  CardSkeleton,
  EmptyState,
  ErrorState,
  OfflineState,
} from "../../../../../components/states";
import { StatusChip } from "../../../../../components/status-chip";
import { RequestError, requestIdOf } from "../../../../../lib/request";
import { MutationFailureNote } from "../../mutation-failure-note";
import { useAttemptKey } from "../../lib/attempt-key";
import { useReadsHeldOffline } from "../../lib/reads";
import { canCurateTags, isSalesReadRefused, useSalesPermissions } from "../../hooks/permissions";
import {
  useArchiveTag,
  useCreateTag,
  useRenameTag,
  useTagCatalog,
  type SalesTag,
} from "../../hooks/crm-queries";
import { useLoadedPager } from "../../../../../lib/cursor-pages";
import { bookPager } from "../../hooks/book-pages";

/** The longest tag name, in code points after trim (spec 09 §2.27). */
export const TAG_NAME_MAX = 40;

/** Why a typed name would be refused before it is sent, or undefined. The
 * contract's own rule: 1 to 40 code points after trim, no `;`, no NUL. */
export function tagNameProblem(raw: string): "empty" | "invalid" | undefined {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return "empty";
  if (Array.from(trimmed).length > TAG_NAME_MAX) return "invalid";
  if (trimmed.includes(";") || trimmed.includes("\u0000")) return "invalid";
  return undefined;
}

/** The field-level message key for a write's refusal, or undefined when the
 * refusal is not about the name. */
export function tagWriteFieldKey(error: unknown): string | undefined {
  if (!(error instanceof RequestError)) return undefined;
  if (error.code === "TAG_NAME_TAKEN") return "sales.tags.nameTaken";
  if (error.code === "TAG_LIMIT_REACHED") return "sales.tags.limitReached";
  if (error.httpStatus === 400) return "sales.tags.nameInvalid";
  return undefined;
}

export function TagsCard() {
  const perms = useSalesPermissions();
  // The mirror KNOWS this lineage is refused the tag read: no card, no read.
  if (isSalesReadRefused(perms, perms.canOpenCustomers)) return null;
  return <TagsCardBody />;
}

function TagsCardBody() {
  const { t, i18n } = useTranslation();
  const perms = useSalesPermissions();
  const canCurate = canCurateTags(perms);
  const offline = useReadsHeldOffline();
  const [showArchived, setShowArchived] = useState(false);
  const catalog = useTagCatalog(showArchived ? "all" : "none", true);
  const create = useCreateTag();
  const createKey = useAttemptKey();
  const rename = useRenameTag();
  const archive = useArchiveTag();
  const [newName, setNewName] = useState("");
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const [confirmingArchive, setConfirmingArchive] = useState<string | null>(null);

  if (catalog.error instanceof RequestError && catalog.error.httpStatus === 403) {
    return (
      <p className="sales-hint" data-testid="sales-settings-tags-refused">
        {t("sales.tags.refused")}
      </p>
    );
  }

  // One page at a time (D-560): Next reads the following page when it is not
  // loaded yet; turning Show archived on or off starts again at page 1.
  const tagPages = useLoadedPager(catalog, showArchived ? "all" : "none");
  const tags: readonly SalesTag[] = tagPages.view.items;
  const newProblem = tagNameProblem(newName);
  const createFieldKey = create.isError ? tagWriteFieldKey(create.error) : undefined;
  const renameProblem = renaming === null ? undefined : tagNameProblem(renaming.name);
  const renameFieldKey = rename.isError ? tagWriteFieldKey(rename.error) : undefined;

  const columns: DataTableColumn<SalesTag>[] = [
    {
      key: "name",
      header: t("sales.tags.colName"),
      id: true,
      render: (tag) =>
        renaming !== null && renaming.id === tag.id ? (
          <span className="sales-tag-rename">
            <input
              value={renaming.name}
              maxLength={200}
              aria-label={t("sales.tags.renameLabel", { name: tag.name })}
              aria-invalid={
                renameProblem === "invalid" || renameFieldKey !== undefined || undefined
              }
              data-testid="sales-settings-tag-rename-input"
              onChange={(event) => {
                rename.reset();
                setRenaming({ id: tag.id, name: event.target.value });
              }}
            />
            {renameProblem === "invalid" || renameFieldKey !== undefined ? (
              <span
                className="field-error"
                role="alert"
                data-testid="sales-settings-tag-rename-error"
              >
                {t(renameFieldKey ?? "sales.tags.nameInvalid")}
              </span>
            ) : null}
          </span>
        ) : (
          <StatusChip variant="neutral" dot={false} className="sales-tag-chip">
            {tag.name}
          </StatusChip>
        ),
    },
    {
      key: "status",
      header: t("sales.tags.colStatus"),
      render: (tag) =>
        tag.status === "archived" ? t("sales.tags.statusArchived") : t("sales.tags.statusActive"),
    },
    ...(canCurate
      ? [
          {
            key: "actions",
            header: t("sales.tags.colActions"),
            actions: true,
            render: (tag: SalesTag) => {
              if (renaming !== null && renaming.id === tag.id) {
                return (
                  <span className="sales-tag-actions">
                    <Button
                      size="sm"
                      data-testid="sales-settings-tag-rename-save"
                      disabled={
                        offline || renameProblem !== undefined || renaming.name.trim() === tag.name
                      }
                      busy={rename.isPending}
                      busyLabel={t("sales.common.saving")}
                      onClick={() => {
                        rename.mutate(
                          { id: tag.id, name: renaming.name.trim() },
                          {
                            onSuccess: () => {
                              setRenaming(null);
                            },
                          },
                        );
                      }}
                    >
                      {t("sales.common.save")}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        rename.reset();
                        setRenaming(null);
                      }}
                    >
                      {t("sales.common.cancel")}
                    </Button>
                  </span>
                );
              }
              if (confirmingArchive === tag.id) {
                return (
                  <span
                    className="sales-tag-actions"
                    role="group"
                    data-testid="sales-settings-tag-archive-confirm"
                  >
                    <span className="sales-tag-confirm-text">
                      {t("sales.tags.archiveConfirm", { name: tag.name })}
                    </span>
                    <Button
                      size="sm"
                      variant="danger"
                      data-testid="sales-settings-tag-archive-yes"
                      disabled={offline || archive.isPending}
                      busy={archive.isPending && archivingId === tag.id}
                      busyLabel={t("sales.tags.archiving")}
                      onClick={() => {
                        setArchivingId(tag.id);
                        archive.mutate(tag.id, {
                          onSettled: () => {
                            setConfirmingArchive(null);
                          },
                        });
                      }}
                    >
                      {t("sales.tags.archive")}
                    </Button>
                    <Button
                      size="sm"
                      data-testid="sales-settings-tag-archive-cancel"
                      onClick={() => {
                        setConfirmingArchive(null);
                      }}
                    >
                      {t("sales.common.cancel")}
                    </Button>
                  </span>
                );
              }
              return (
                <span className="sales-tag-actions">
                  {/* An archived tag can be renamed too (§2.27): it still
                      shows on the customers that carry it, so a typo in it
                      stays fixable. A name an active tag holds answers
                      TAG_NAME_TAKEN, which reads on the field. */}
                  <Button
                    size="sm"
                    data-testid="sales-settings-tag-rename"
                    disabled={offline}
                    onClick={() => {
                      rename.reset();
                      setRenaming({ id: tag.id, name: tag.name });
                    }}
                  >
                    {t("sales.tags.rename")}
                  </Button>
                  {tag.status === "active" ? (
                    <Button
                      size="sm"
                      variant="danger"
                      data-testid="sales-settings-tag-archive"
                      disabled={offline || archive.isPending}
                      onClick={() => {
                        archive.reset();
                        setConfirmingArchive(tag.id);
                      }}
                    >
                      {t("sales.tags.archive")}
                    </Button>
                  ) : null}
                </span>
              );
            },
          } satisfies DataTableColumn<SalesTag>,
        ]
      : []),
  ];

  const pausedOffline = catalog.fetchStatus === "paused";

  return (
    <Card
      className="sales-360-card sales-settings-tags"
      data-testid="sales-settings-tags"
      title={t("sales.tags.title")}
      action={
        <HelpTip
          label={t("nav.helpAbout", { topic: t("sales.tags.title") })}
          data-testid="sales-settings-tags-help"
        >
          {t("sales.tags.cardBody")}
        </HelpTip>
      }
    >
      {canCurate ? (
        <div className="sales-tag-new">
          <Field label={t("sales.tags.newLabel")}>
            <input
              value={newName}
              maxLength={200}
              aria-invalid={newProblem === "invalid" || createFieldKey !== undefined || undefined}
              data-testid="sales-settings-tag-new-name"
              onChange={(event) => {
                create.reset();
                createKey.reset();
                setNewName(event.target.value);
              }}
            />
          </Field>
          <Button
            size="sm"
            data-testid="sales-settings-tag-create"
            disabled={offline || newProblem !== undefined}
            busy={create.isPending}
            busyLabel={t("sales.tags.creating")}
            onClick={() => {
              create.mutate(
                { name: newName.trim(), key: createKey.take() },
                {
                  onSuccess: () => {
                    createKey.reset();
                    setNewName("");
                  },
                  onError: (error) => {
                    createKey.failed(error);
                  },
                },
              );
            }}
          >
            {t("sales.tags.new")}
          </Button>
          {newProblem === "invalid" || createFieldKey !== undefined ? (
            <span className="field-error" role="alert" data-testid="sales-settings-tag-new-error">
              {t(createFieldKey ?? "sales.tags.nameInvalid")}
            </span>
          ) : null}
        </div>
      ) : (
        <p className="sales-hint" data-testid="sales-settings-tags-readonly">
          {t("sales.tags.readOnly")}
        </p>
      )}

      <CheckboxField
        label={t("sales.tags.showArchived")}
        checked={showArchived}
        disabled={offline}
        data-testid="sales-settings-tags-show-archived"
        onChange={setShowArchived}
      />

      {catalog.data === undefined ? (
        catalog.error !== null ? (
          <ErrorState
            title={t("sales.tags.error")}
            retryLabel={t("sales.common.retry")}
            requestIdLabel={t("sales.common.requestId")}
            requestId={requestIdOf(catalog.error)}
            onRetry={() => {
              void catalog.refetch();
            }}
          />
        ) : pausedOffline ? (
          <div data-testid="sales-settings-tags-offline">
            <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />
          </div>
        ) : (
          <CardSkeleton lines={4} label={t("sales.tags.loading")} />
        )
      ) : tags.length === 0 ? (
        showArchived ? (
          <div data-testid="sales-settings-tags-empty" data-scope="all">
            <EmptyState
              title={t("sales.tags.emptyTitle")}
              body={canCurate ? t("sales.tags.emptyBodyCurator") : t("sales.tags.emptyBodyReader")}
            />
          </div>
        ) : (
          <div data-testid="sales-settings-tags-empty" data-scope="active">
            <EmptyState
              title={t("sales.tags.emptyActiveTitle")}
              body={
                canCurate
                  ? t("sales.tags.emptyActiveBodyCurator")
                  : t("sales.tags.emptyActiveBodyReader")
              }
            />
          </div>
        )
      ) : (
        <>
          {offline ? (
            <p className="sales-hint" data-testid="sales-settings-tags-offline-note">
              {t("sales.offline.body")}
            </p>
          ) : null}
          <DataTable
            data-testid="sales-settings-tag-table"
            columns={columns}
            rows={tags}
            rowKey={(tag) => tag.id}
            caption={t("sales.tags.caption")}
            rowTestId="sales-settings-tag-row"
            rowProps={(tag) => ({ "data-tag-id": tag.id, "data-status": tag.status })}
            empty={null}
            pager={bookPager(
              t,
              i18n.language,
              { ...tagPages.view, hasNext: tagPages.view.hasNext && !offline },
              {
                turning: tagPages.turning,
                onNext: tagPages.next,
                onPrev: tagPages.prev,
              },
            )}
          />
        </>
      )}

      {archive.isError ? (
        <MutationFailureNote
          data-testid="sales-settings-tag-archive-failed"
          text={t("sales.tags.saveFailed")}
          error={archive.error}
        />
      ) : null}
      {create.isError && createFieldKey === undefined ? (
        <MutationFailureNote
          data-testid="sales-settings-tag-create-failed"
          text={t("sales.tags.saveFailed")}
          error={create.error}
        />
      ) : null}
      {rename.isError && renameFieldKey === undefined ? (
        <MutationFailureNote
          data-testid="sales-settings-tag-rename-failed"
          text={t("sales.tags.saveFailed")}
          error={rename.error}
        />
      ) : null}
    </Card>
  );
}
